import { useAuthStore } from '../stores/auth.store.ts';

const BASE_URL = '/api';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const { token } = useAuthStore.getState();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Auth
export async function getSession() {
  return fetchApi<{ success: boolean; data: any; error?: string }>('/auth/session');
}


// Calendar / Appointments
export async function getAppointments(params: {
  workspaceId: string;
  dates: string[];
  locationId: string;
  resources: Array<{ id: string; type: string }>;
  withCancelled?: boolean;
}) {
  return fetchApi<{ data: any }>('/calendar', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

// Employees
export async function getEmployees(workspaceId: string, locationId?: string) {
  const qs = new URLSearchParams({
    workspaceId,
    ...(locationId && locationId !== 'all' && { locationId }),
  });
  return fetchApi<{ data: any }>(`/employees?${qs}`);
}

// Booking detail
export async function getBookingDetail(bookingId: string, workspaceId: string) {
  return fetchApi<{ data: any; included?: any[] }>(`/bookings/${bookingId}?workspaceId=${workspaceId}`);
}

// Orders
export async function getOrder(orderId: string, workspaceId: string) {
  return fetchApi<{ data: any }>('/orders', {
    method: 'POST',
    body: JSON.stringify({ workspaceId, orderId }),
  });
}

// Campaigns (retention report)
export async function getCampaigns(params: {
  workspaceId: string;
  limit?: number;
  offset?: number;
  dateFrom?: string;
  dateTo?: string;
}) {
  const qs = new URLSearchParams({
    workspaceId: params.workspaceId,
    limit: String(params.limit || 100),
    offset: String(params.offset || 0),
    ...(params.dateFrom && { dateFrom: params.dateFrom }),
    ...(params.dateTo && { dateTo: params.dateTo }),
  });
  return fetchApi<{ data: any }>(`/campaigns?${qs}`);
}

// Customer search
export async function searchCustomers(params: {
  workspaceId: string;
  limit?: number;
  offset?: number;
  segmentId?: string;
}) {
  const qs = new URLSearchParams({
    workspaceId: params.workspaceId,
    limit: String(params.limit || 50),
    offset: String(params.offset || 0),
    ...(params.segmentId && { segmentId: params.segmentId }),
  });
  return fetchApi<{ data: any }>(`/customers?${qs}`);
}

// Customer stats
export async function getCustomerStats(customerId: string, workspaceId: string) {
  return fetchApi<{ data: any }>(`/customers/${customerId}/stats?workspaceId=${workspaceId}`);
}

// Customer recent appointments
export async function getCustomerAppointments(customerId: string, workspaceId: string, locationId: string, limit = 11) {
  return fetchApi<{ data: any }>(`/customers/${customerId}/appointments?workspaceId=${workspaceId}&locationId=${locationId}&limit=${limit}`);
}

// Customer sales
export async function getCustomerSales(customerId: string, workspaceId: string) {
  return fetchApi<{ data: any; included?: any[] }>(`/customers/${customerId}/sales?workspaceId=${workspaceId}`);
}


// Segments
export async function getSegments(workspaceId: string) {
  return fetchApi<{ data: any }>('/segments', {
    method: 'POST',
    body: JSON.stringify({ workspaceId }),
  });
}

// Dashboard
export async function getDashboard(workspaceId: string) {
  return fetchApi<{ success: boolean; data: any }>(`/dashboard?workspaceId=${workspaceId}`);
}

// Rates
export async function getRates(type: 'usd' | 'eur' | 'all' = 'all') {
  return fetchApi<{ success: boolean; data: any }>(`/rates?type=${type}`);
}

// Report Logs (Delta Sync)
export async function getAppointmentLog(params: {
  workspaceId: string;
  limit?: number;
  offset?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const qs = new URLSearchParams({
    workspaceId: params.workspaceId,
    limit: String(params.limit || 25),
    offset: String(params.offset || 0),
    ...(params.locationId && { locationId: params.locationId }),
    ...(params.dateFrom && { dateFrom: params.dateFrom }),
    ...(params.dateTo && { dateTo: params.dateTo }),
  });
  return fetchApi<{ data: any }>(`/reports/appointment-log?${qs}`);
}

export async function getSalesLog(params: {
  workspaceId: string;
  limit?: number;
  offset?: number;
  dateFrom?: string;
  dateTo?: string;
}) {
  const qs = new URLSearchParams({
    workspaceId: params.workspaceId,
    limit: String(params.limit || 25),
    offset: String(params.offset || 0),
    ...(params.dateFrom && { dateFrom: params.dateFrom }),
    ...(params.dateTo && { dateTo: params.dateTo }),
  });
  return fetchApi<{ data: any }>(`/reports/sales-log?${qs}`);
}
